import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { can } from '@gwc/shared';
import { tastingsApi, type CreateEventInput } from '../api/tastings';
import { categoriesForSave, defaultEventSetup, type EventSetupValue } from '../api/rsvp';
import type { EventRow } from '../api/models';
import { useAuth } from '../auth/AuthProvider';
import { eventDate, PHASE_LABEL } from '../lib/format';
import { EventSetupFields } from '../components/EventSetupFields';
import { TestEventBadge } from '../components/TestEventBadge';
import { ApiError } from '../lib/api';

export function Tastings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canManage = user ? can(user.roles, 'tasting:create') : false;
  const [showForm, setShowForm] = useState(false);

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: () => tastingsApi.list(),
  });

  const active = events.filter((e) => e.status === 'live' || e.status === 'upcoming');
  const done = events.filter((e) => e.status === 'completed' || e.status === 'archived');

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-display text-3xl text-cream-50">Tastings</h1>
        {canManage && (
          <button className="btn-primary" onClick={() => setShowForm((s) => !s)}>
            {showForm ? 'Cancel' : 'New Tasting'}
          </button>
        )}
      </div>

      {showForm && <NewTastingForm onDone={() => { setShowForm(false); void qc.invalidateQueries({ queryKey: ['events'] }); }} />}

      {isLoading ? (
        <p className="text-cream-300/60">Loading…</p>
      ) : (
        <div className="space-y-8">
          <Section title="Live & upcoming" events={active} canManage={canManage} empty="No active tastings." />
          {done.length > 0 && (
            <Section title="Recently completed" events={done} canManage={canManage} empty="" />
          )}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  events,
  canManage,
  empty,
}: {
  title: string;
  events: EventRow[];
  canManage: boolean;
  empty: string;
}) {
  return (
    <div>
      <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">{title}</h2>
      {events.length === 0 ? (
        <p className="text-sm text-cream-300/50">{empty}</p>
      ) : (
        <div className="space-y-3">
          {events.map((e) => (
            <div key={e.id} className="card flex flex-wrap items-center justify-between gap-3 py-4">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-lg text-cream-50">{e.title}</span>
                  <TestEventBadge isTest={e.isTest} />
                </div>
                <div className="text-xs text-cream-300/60">
                  {eventDate(e)} · {e.regionLocation ?? 'Location TBD'} ·{' '}
                  <span className="text-gold-300">{PHASE_LABEL[e.phase] ?? e.phase}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Link to={`/tastings/${e.id}`} className="btn-ghost px-3 py-1 text-xs">
                  Details &amp; RSVP
                </Link>
                {(e.phase === 'scoring_open' || e.status === 'live') && (
                  <Link to={`/live/${e.id}`} className="btn-primary px-3 py-1 text-xs">
                    Score now
                  </Link>
                )}
                {canManage && (
                  <Link to={`/tastings/${e.id}/setup`} className="btn-ghost px-3 py-1 text-xs">
                    Setup
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NewTastingForm({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState<CreateEventInput>({
    title: '',
    theme: '',
    regionLocation: '',
    venue: '',
    dressCode: '',
    capacity: null,
    eventYear: new Date().getFullYear(),
    eventMonth: new Date().getMonth() + 1,
    eventDate: new Date().toISOString().slice(0, 10),
    datePrecision: 'day',
    isScored: true,
    announce: true,
    reminderDays: [14, 3],
    isTest: false,
  });
  const [setup, setSetup] = useState<EventSetupValue>(defaultEventSetup);
  const [reminderText, setReminderText] = useState('14, 3');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => {
      const wineCategories = categoriesForSave(setup.wineCategories);
      return tastingsApi.create({
        ...form,
        worldScope: setup.worldScope,
        rules: setup.rules.trim() || undefined,
        invitedMemberTypes: setup.invitedMemberTypes,
        price: setup.price,
        requiresPayment: setup.requiresPayment,
        requiresDuesPaid: setup.requiresDuesPaid,
        cancellationCutoffDays: setup.cancellationCutoffDays,
        wineCategories: wineCategories.length ? wineCategories : undefined,
      });
    },
    onSuccess: () => onDone(),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create tasting'),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (setup.requiresPayment && !(setup.price != null && setup.price > 0)) {
      setError('Set a price to take payment on RSVP.');
      return;
    }
    create.mutate();
  }
  const set = (patch: Partial<CreateEventInput>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <form onSubmit={submit} className="card mb-6 space-y-4">
      {error && <p className="text-sm text-wine-300">{error}</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">Title</span>
          <input className="field" value={form.title} onChange={(e) => set({ title: e.target.value })} required />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">Theme</span>
          <input className="field" value={form.theme} onChange={(e) => set({ theme: e.target.value })} placeholder="Napa Cabernet" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">Region / Location</span>
          <input className="field" value={form.regionLocation} onChange={(e) => set({ regionLocation: e.target.value })} placeholder="Monterey Peninsula" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">Venue</span>
          <input className="field" value={form.venue} onChange={(e) => set({ venue: e.target.value })} placeholder="L'Escargot" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">Date</span>
          <input type="date" className="field" value={form.eventDate ?? ''} onChange={(e) => {
            const d = e.target.value;
            set({ eventDate: d, eventYear: Number(d.slice(0, 4)), eventMonth: Number(d.slice(5, 7)) });
          }} required />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">Dress code</span>
          <input className="field" value={form.dressCode} onChange={(e) => set({ dressCode: e.target.value })} placeholder="Dark suit" />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs uppercase tracking-wide text-cream-300/70">Capacity (optional)</span>
          <input type="number" min={1} className="field" value={form.capacity ?? ''} onChange={(e) => set({ capacity: e.target.value ? Number(e.target.value) : null })} placeholder="No limit" />
        </label>
      </div>
      <EventSetupFields value={setup} onChange={setSetup} disabled={create.isPending} />
      <div className="grid gap-4 rounded-lg border border-ink-800 p-4 sm:grid-cols-2">
        <label className="flex items-start gap-3 text-sm text-cream-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-gold-500"
            checked={(form.announce ?? true) && !form.isTest}
            disabled={form.isTest}
            onChange={(e) => set({ announce: e.target.checked })}
          />
          <span className={form.isTest ? 'opacity-50' : undefined}>
            Announce to the invited members now
            <span className="block text-xs text-cream-300/50">
              {form.isTest
                ? 'Not available on a test event — announce it from the tasting page once you have added testers.'
                : 'Sends the "save the date" email and push. Untick to finish setting up first — you can announce from the tasting page later.'}
            </span>
          </span>
        </label>
        <label className="block text-sm text-cream-200">
          RSVP reminders (days before)
          <input
            className="field mt-1"
            value={reminderText}
            onChange={(e) => {
              setReminderText(e.target.value);
              const days = e.target.value
                .split(/[,\s]+/)
                .map((x) => Number(x))
                .filter((n) => Number.isInteger(n) && n >= 0 && n <= 90);
              set({ reminderDays: [...new Set(days)] });
            }}
            placeholder="14, 3"
          />
          <span className="block text-xs text-cream-300/50">
            Members who haven't replied are nudged on each of these days. Leave empty for none.
          </span>
        </label>
      </div>
      <div className="rounded-lg border border-gold-500/30 bg-gold-500/5 p-4">
        <label className="flex items-start gap-3 text-sm text-cream-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 accent-gold-500"
            checked={form.isTest ?? false}
            onChange={(e) => set({ isTest: e.target.checked, announce: false })}
          />
          <span>
            Make this a test event
            <span className="block text-xs text-cream-300/50">
              A full rehearsal: RSVP, scoring, the reveal and comms all work exactly as they do on
              the night. It stays out of the archive, the club statistics and every export, and it
              only ever emails the testers you add to it — never the whole club. You can switch it
              on or off from Tasting Setup until its results go out.
            </span>
          </span>
        </label>
      </div>
      <button type="submit" className="btn-primary" disabled={create.isPending}>
        {create.isPending
          ? 'Creating…'
          : form.isTest
            ? 'Create test event'
            : 'Create tasting'}
      </button>
    </form>
  );
}
